import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { PlusIcon } from '@phosphor-icons/react/dist/csr/Plus';

import { ReplacementFormDialog } from './ReplacementFormDialog';
import { DEVICE_TYPE_LABEL_KEY, QUEUE_EMPTY_KEY, QUEUE_LABEL_KEY, STATUS_LABEL_KEY } from './replacementLabels';
import { PageActions } from '../../app/shell/PageActions';
import { FilterBar } from '../../design-system/composites/FilterBar/FilterBar';
import { Button } from '../../design-system/primitives/Button/Button';
import {
  DataTable,
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import { Pagination } from '../../design-system/composites/Pagination/Pagination';
import { useCursorPages } from '../../design-system/composites/Pagination/useCursorPages';
import { Input } from '../../design-system/primitives/Input/Input';
import { SegmentedControl } from '../../design-system/primitives/SegmentedControl/SegmentedControl';
import { Select } from '../../design-system/primitives/Select/Select';
import { ExportButton } from '../shared/ExportButton';
import { ApiError } from '../../lib/api/client';
import {
  replacementsApi,
  type ReplacementDeviceType,
  type ReplacementQuery,
  type ReplacementRow,
  type ReplacementStatusFilter,
} from '../../lib/api/replacements';
import { formatTimestamp } from '../../lib/format/dateTime';
import { mapDetails } from '../reference/referenceFormHelpers';
import { canReadReplacements } from '../../lib/permissions/replacements';
import styles from './ReplacementList.module.css';

/**
 * The replacements queue (M-13, Task 5, spec §5). Copies
 * `JustificationList.tsx`'s structure — the most recently reviewed screen,
 * carrying the fixes M-10 and M-11 paid for: a segmented control over the
 * server's own status filter, a draft/applied filter row, a
 * cursor-paginated table.
 *
 * ## `requested` is the starting segment, and it is proven, not assumed
 *
 * Unlike `JustificationList.tsx`'s `all`, this screen starts on
 * `requested` — the work queue, and the server's own default. THAT DEFAULT
 * IS CHECKED AGAINST A FRESHLY CREATED ROW BY A TEST, not by reasoning:
 * spec §0 is the legacy making exactly this mistake in the opposite
 * direction, where `WHERE Status = 'active'`
 * (`admin-show-active-replacements.php:342`) has matched nothing since the
 * create form was written, because that form writes `''`. M-09 shipped the
 * same shape in this codebase.
 *
 * ## Every filter is a server parameter
 *
 * `status`, `case_number`, `device_type` and `mine` are all real
 * `ReplacementIndexRequest` filters. NONE of them narrows the page already
 * returned. M-11 shipped a "Raised by me" that did exactly that, and on a
 * cursor-paginated list it told a caller there were no matches when the
 * matches were simply on page 2.
 *
 * ## `mine` serialises `1`/`0`
 *
 * `replacements.ts`'s own `queryString()` carries it. Laravel's `boolean`
 * rule rejects the string `'true'`, and a client sending `String(true)`
 * gets a `422` for the whole request — that defect was live in four
 * modules at once before M-12 caught it.
 *
 * ## A newly raised replacement is always `requested`
 *
 * Prepending it unconditionally would put a `requested` row into whichever
 * queue happened to be selected, including "Dispensed" and "Rejected" — the
 * identical lesson `EscalationList.tsx`'s and `JustificationList.tsx`'s own
 * `handleCreated` state. It belongs only in the queues that would ever show
 * one.
 */

export interface ReplacementListProps {
  permissions: readonly string[];
  /** Bumped by the screen when a detail action changes a row, so the queue refetches without being re-mounted (and so keeps its filters). */
  refreshKey?: number;
  onOpen: (uuid: string) => void;
  /**
   * Whether this list is the view on screen. The screen keeps the list
   * mounted (hidden) under an opened record, and `PageActions` portals into
   * the shell's title row whatever `hidden` says — so a hidden list withdraws
   * its page commands, or they would sit beside the detail.
   */
  active?: boolean;
}

interface ReplacementListRow extends Row, ReplacementRow {
  /** A flattened, plain-string duplicate of `case.case_number` — `DataTable`'s own `leadingCell()` only turns the FIRST column into the clickable opener when that column carries no `render`, and a `render` would be required for `case`, which is an object rather than a primitive `row[column.key]` could print directly. */
  case_number: string;
}

function toRow(node: ReplacementRow): ReplacementListRow {
  return { ...node, id: node.uuid, case_number: node.case.case_number };
}

const QUEUES: readonly ReplacementStatusFilter[] = ['requested', 'dispensed', 'rejected', 'all'];

interface FilterState {
  caseNumber: string;
  deviceType: '' | ReplacementDeviceType;
  mine: boolean;
}

const BLANK_FILTERS: FilterState = { caseNumber: '', deviceType: '', mine: false };

function toQuery(
  status: ReplacementStatusFilter,
  filters: FilterState,
  cursor?: string,
  perPage?: number,
): ReplacementQuery {
  const query: ReplacementQuery = { status };
  if (filters.caseNumber.trim() !== '') query.case_number = filters.caseNumber.trim();
  if (filters.deviceType !== '') query.device_type = filters.deviceType;
  if (filters.mine) query.mine = true;
  if (cursor !== undefined) query.cursor = cursor;
  if (perPage !== undefined) query.per_page = perPage;
  return query;
}

/** Read off the OUTGOING QUERY, not `appliedFilters` state — `JustificationList.tsx`'s own `hasAppliedFilters` shape. `status` is excluded: it always travels. */
function hasAppliedFilters(query: ReplacementQuery): boolean {
  return query.case_number !== undefined || query.device_type !== undefined || query.mine !== undefined;
}

export function ReplacementList({ permissions, refreshKey = 0, onOpen, active = true }: ReplacementListProps) {
  const { t, i18n } = useTranslation();
  const canCreate = permissions.includes('replacement.create');

  const [statusFilter, setStatusFilter] = useState<ReplacementStatusFilter>('requested');
  const [status, setStatus] = useState<ListStatus>('loading');
  const [fetchedRows, setFetchedRows] = useState<readonly ReplacementListRow[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  /**
   * `case_number` is free text, validated `string|max:250` server-side. A
   * cursor that no longer decodes is the other keyed failure this list can
   * produce. Discarding `error.details` would leave only the generic
   * "queue could not be loaded", with a Retry that resends the same
   * invalid filter forever.
   */
  const [caseNumberError, setCaseNumberError] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  // 50 — `ReplacementIndexRequest::DEFAULT_PER_PAGE` server-side.
  const pages = useCursorPages();
  // Destructured — `pages` is a fresh object every render; see
  // `CaseList.tsx`'s identical note.
  const { observe } = pages;
  const [draftFilters, setDraftFilters] = useState<FilterState>(BLANK_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(BLANK_FILTERS);
  const [formOpen, setFormOpen] = useState(false);

  const requestSeq = useRef(0);

  const load = useCallback(async (query: ReplacementQuery) => {
    const seq = ++requestSeq.current;
    setStatus('loading');
    setReference(null);
    setCaseNumberError(null);

    try {
      const page = await replacementsApi.list(query);
      if (seq !== requestSeq.current) return;
      setFetchedRows(page.data.map(toRow));
      setTotal(page.meta.total);
      observe({ next_cursor: page.meta.next_cursor, total: page.meta.total });
      setStatus(page.data.length === 0 ? (hasAppliedFilters(query) ? 'no-results' : 'empty') : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (seq !== requestSeq.current) return;
      setReference(error.requestId ?? null);
      setCaseNumberError(mapDetails(error.details)['case_number'] ?? null);
      setStatus('error');
    }
  }, [observe]);

  useEffect(() => {
    void load(toQuery(statusFilter, appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }, [load, statusFilter, appliedFilters, pages.cursor, pages.perPage, refreshKey]);

  function handleStatusChange(value: string): void {
    setStatusFilter(value as ReplacementStatusFilter);
    pages.first();
  }

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    pages.first();
    setAppliedFilters(draftFilters);
  }

  function handleClearFilters(): void {
    setDraftFilters(BLANK_FILTERS);
    pages.first();
    setAppliedFilters(BLANK_FILTERS);
  }

  function retry(): void {
    void load(toQuery(statusFilter, appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }

  // A freshly raised replacement is ALWAYS `requested`
  // (`ReplacementController::store()`) — see this file's own docblock.
  function handleCreated(created: ReplacementRow): void {
    if (statusFilter === 'requested' || statusFilter === 'all') {
      setFetchedRows((current) => [toRow(created), ...current]);
      if (status === 'empty') setStatus('ready');
    }
    setFormOpen(false);
  }

  function renderDeviceType(row: Row): ReactNode {
    return t(DEVICE_TYPE_LABEL_KEY[(row as unknown as ReplacementRow).device_type]);
  }

  function renderStatus(row: Row): ReactNode {
    return t(STATUS_LABEL_KEY[(row as unknown as ReplacementRow).status]);
  }

  function renderRequestedAt(row: Row): ReactNode {
    const value = (row as unknown as ReplacementRow).requested_at;
    return value === null ? null : formatTimestamp(value, i18n.language);
  }

  function renderSerial(row: Row): ReactNode {
    return (row as unknown as ReplacementRow).old_serial_number ?? t('table.notRecorded');
  }

  /**
   * What was handed over. `new_serial_number` before `new_device`, for the
   * reason `ReplacementDetail` gives: the registry row is absent for almost
   * every real replacement, migrated ones included.
   */
  function renderNewSerial(row: Row): ReactNode {
    const record = row as unknown as ReplacementRow;

    return record.new_serial_number ?? record.new_device?.serial_number ?? t('table.notRecorded');
  }

  const queueOptions = QUEUES.map((value) => ({ value, label: t(QUEUE_LABEL_KEY[value]) }));

  const columns: readonly Column[] = [
    // NO `render` HERE, DELIBERATELY — `DataTable`'s own `leadingCell()`
    // only turns the FIRST column into the clickable row opener when it
    // carries no `render` (this file's own `ReplacementListRow` docblock).
    { key: 'case_number', label: t('replacements.fieldCase') },
    { key: 'device_type', label: t('replacements.fieldDeviceType'), render: renderDeviceType },
    { key: 'old_serial_number', label: t('replacements.fieldOldSerial'), render: renderSerial },
    { key: 'new_serial_number', label: t('replacements.fieldNewSerial'), render: renderNewSerial },
    { key: 'requested_at', label: t('replacements.fieldRequestedAt'), render: renderRequestedAt },
    { key: 'status', label: t('table.status'), render: renderStatus },
  ];

  return (
    <div className={styles['page']}>
      {/* No second heading under the shell's `<h1>`: "New replacement" and
          Export sit in the title row. `statusFilter` travels with the export
          filters: the server defaults `status` to `requested`, not `all`, so
          an export that omitted it would not be the queue on screen. No
          cursor. */}
      {active ? (
        <PageActions>
          {canCreate ? (
            <Button
              onClick={() => {
                setFormOpen(true);
              }}
              icon={<PlusIcon size={16} />}
            >
              {t('replacements.newReplacement')}
            </Button>
          ) : null}
          <ExportButton
            module="replacements"
            filters={{ ...toQuery(statusFilter, appliedFilters) }}
            canExport={canReadReplacements(permissions)}
          />
        </PageActions>
      ) : null}

      <FilterBar
        onSubmit={handleFilterSubmit}
        onClear={handleClearFilters}
        leading={
          <SegmentedControl
            label={t('replacements.queueLabel')}
            options={queueOptions}
            value={statusFilter}
            onChange={handleStatusChange}
          />
        }
      >
        <Input
          label={t('replacements.caseFilterLabel')}
          width="phone"
          dir="ltr"
          value={draftFilters.caseNumber}
          error={caseNumberError ?? undefined}
          onChange={(event) => {
            const { value } = event.target;
            setDraftFilters((current) => ({ ...current, caseNumber: value }));
            setCaseNumberError(null);
          }}
        />

        <Select
          label={t('replacements.fieldDeviceType')}
          width="phone"
          value={draftFilters.deviceType}
          options={[
            { value: '', label: t('replacements.anyDeviceType') },
            { value: 'sensor', label: t('replacements.deviceType.sensor') },
            { value: 'reader', label: t('replacements.deviceType.reader') },
          ]}
          onChange={(event) => {
            const value = event.target.value as '' | ReplacementDeviceType;
            setDraftFilters((current) => ({ ...current, deviceType: value }));
          }}
        />

        <label className={styles['mineOption']}>
          <input
            type="checkbox"
            checked={draftFilters.mine}
            onChange={(event) => {
              const { checked } = event.target;
              setDraftFilters((current) => ({ ...current, mine: checked }));
            }}
          />
          {t('replacements.raisedByMe')}
        </label>
      </FilterBar>

      <DataTable
        caption={t('replacements.tableCaption')}
        columns={columns}
        rows={fetchedRows}
        status={status}
        empty={{ body: t(QUEUE_EMPTY_KEY[statusFilter]) }}
        noResults={{ title: t('list.noResultsTitle') }}
        onClearFilters={handleClearFilters}
        error={{ title: t('replacements.queueLoadFailed') }}
        onRowActivate={(row) => {
          onOpen(row.id);
        }}
      />

      <Pagination
        pages={pages}
        total={total}
        rowsOnPage={fetchedRows.length}
      />

      {status === 'error' ? (
        <div className={styles['recovery']}>
          <Button variant="secondary" onClick={retry}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('actions.retry')}
          </Button>

          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="replacementListRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}

      <ReplacementFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
        }}
        onSaved={handleCreated}
      />
    </div>
  );
}
